import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { colors, baseStyles } from '../styles';
import { ActionButtons } from '../components';
import { getFavorites, removeFavorite } from '../services/favoritesService';

const styles = {
  ...baseStyles,
  headerRight: {
    marginLeft: 'auto',
  },
  editButton: {
    padding: '0.5rem 0.75rem',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 500,
    color: colors.accent,
    transition: 'opacity 0.2s',
  },
  groupHeader: {
    fontSize: '0.75rem',
    fontWeight: 600,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '0.75rem 0',
    marginTop: '1rem',
    borderTop: `1px solid ${colors.border}`,
  },
  firstGroupHeader: {
    marginTop: 0,
    borderTop: 'none',
  },
  favoriteCard: {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    padding: '1rem',
    marginBottom: '0.75rem',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
  },
  favoriteContent: {
    flex: 1,
    minWidth: 0,
  },
  favoriteName: {
    fontSize: '0.9375rem',
    fontWeight: 600,
    color: colors.textPrimary,
    margin: 0,
    marginBottom: '0.25rem',
  },
  favoriteMeta: {
    fontSize: '0.8rem',
    color: colors.textSecondary,
    marginBottom: '0.5rem',
  },
  deleteButton: {
    width: '32px',
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.errorBg,
    border: `1px solid ${colors.error}`,
    borderRadius: '8px',
    cursor: 'pointer',
    color: colors.error,
    fontSize: '1rem',
    flexShrink: 0,
    transition: 'all 0.2s',
  },
  emptyState: {
    textAlign: 'center',
    padding: '3rem 1rem',
  },
  emptyIcon: {
    fontSize: '3rem',
    marginBottom: '1rem',
    color: colors.textMuted,
  },
  emptyTitle: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: '0.5rem',
  },
  emptyText: {
    fontSize: '0.875rem',
    color: colors.textSecondary,
  },
};

export default function LocalizadorFavoritos() {
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const [favoritos, setFavoritos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [count, setCount] = useState(0);
  const [limit, setLimit] = useState(3);

  // Cargar favoritos desde el backend
  const cargarFavoritos = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // getFavorites con metadata incluye count y limit
      const result = await getFavorites(null, true);
      setFavoritos(result.data || []);
      setCount(result.count || 0);
      setLimit(result.limit || 3);
    } catch (err) {
      console.error('Error cargando favoritos:', err);
      // Redirigir al login si es error de autenticacion
      const errorMsg = err.message?.toLowerCase() || '';
      if (errorMsg.includes('autenticacion') || errorMsg.includes('auth') || errorMsg.includes('401')) {
        navigate('/login', { replace: true });
        return;
      }
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    cargarFavoritos();
  }, [cargarFavoritos]);

  // Agrupar favoritos por perfil
  const gruposPorPerfil = {};

  favoritos.forEach(fav => {
    const grupo = fav.perfilId || 'general';
    if (!gruposPorPerfil[grupo]) {
      gruposPorPerfil[grupo] = [];
    }
    gruposPorPerfil[grupo].push(fav);
  });

  // Construir grupos ordenados desde los datos reales
  // Perfiles con nombre van primero, 'general' va al final
  const ordenGrupos = [];

  Object.entries(gruposPorPerfil).forEach(([id, items]) => {
    if (id !== 'general') {
      const nombre = items[0]?.perfilNombre || items[0]?.perfilTipo || id;
      ordenGrupos.push({ id, nombre, items });
    }
  });

  // Generales al final
  if (gruposPorPerfil['general']) {
    ordenGrupos.push({ id: 'general', nombre: 'Generales', items: gruposPorPerfil['general'] });
  }

  const handleBack = () => {
    navigate(-1);
  };

  const handleDelete = async (favoriteId) => {
    try {
      await removeFavorite(favoriteId);
      // Actualizar lista local
      setFavoritos(prev => prev.filter(f => f.id !== favoriteId));
      setCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Error eliminando favorito:', err);
      alert('Error al eliminar favorito');
    }
  };

  const handleCardClick = (lugarId, perfilId) => {
    if (editMode) return;
    const params = new URLSearchParams();
    if (perfilId && perfilId !== 'general') params.set('perfil', perfilId);
    const query = params.toString() ? `?${params.toString()}` : '';
    navigate(`/localizador/lugar/${lugarId}${query}`);
  };

  const totalFavoritos = favoritos.length;

  return (
    <div style={styles.container}>
      <div style={styles.main}>
        {/* Header */}
        <div style={styles.header}>
          <button style={styles.backButton} onClick={handleBack}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors.textPrimary} strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </button>
          <div>
            <h1 style={styles.title}>
              {editMode ? 'Cancelar' : 'Mis favoritos'}
              {!editMode && !loading && (
                <span style={{
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  color: colors.textSecondary,
                  marginLeft: '0.5rem'
                }}>
                  ({count}/{limit})
                </span>
              )}
            </h1>
          </div>
          <div style={styles.headerRight}>
            {totalFavoritos > 0 && (
              <button
                style={styles.editButton}
                onClick={() => setEditMode(!editMode)}
              >
                {editMode ? 'Listo' : 'Editar'}
              </button>
            )}
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>⏳</div>
            <p style={styles.emptyText}>Cargando favoritos...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>⚠️</div>
            <h3 style={styles.emptyTitle}>Error al cargar</h3>
            <p style={styles.emptyText}>{error}</p>
            <button
              onClick={cargarFavoritos}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                backgroundColor: colors.accent,
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Reintentar
            </button>
          </div>
        )}

        {/* Contenido */}
        {!loading && !error && totalFavoritos === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>☆</div>
            <h3 style={styles.emptyTitle}>Sin favoritos</h3>
            <p style={styles.emptyText}>
              Guarda lugares y proveedores para acceder rápidamente
            </p>
          </div>
        ) : !loading && !error && (
          ordenGrupos.map((grupo, index) => (
            <div key={grupo.id}>
              <h2
                style={{
                  ...styles.groupHeader,
                  ...(index === 0 ? styles.firstGroupHeader : {}),
                }}
              >
                {grupo.nombre}
              </h2>

              {grupo.items.map((item) => (
                <div
                  key={item.id}
                  style={{
                    ...styles.favoriteCard,
                    cursor: editMode ? 'default' : 'pointer',
                  }}
                  onClick={() => handleCardClick(item.lugar?.id || item.placeId, item.perfilId)}
                >
                  <div style={styles.favoriteContent}>
                    <h3 style={styles.favoriteName}>{item.lugar?.nombre || 'Lugar'}</h3>
                    <p style={styles.favoriteMeta}>
                      {item.lugar?.categoriaDisplay || item.lugar?.categoria || ''}
                      {item.lugar?.distancia && ` · ${item.lugar.distancia}`}
                    </p>

                    {!editMode && item.lugar && (item.lugar.telefono || item.lugar.whatsapp || item.lugar.lat) && (
                      <ActionButtons
                        telefono={item.lugar.telefono}
                        whatsapp={item.lugar.whatsapp}
                        lat={item.lugar.lat}
                        lng={item.lugar.lng}
                        direccion={item.lugar.direccion}
                      />
                    )}
                  </div>

                  {editMode && (
                    <button
                      style={styles.deleteButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(item.id);
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
